import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  SkeletonCircle,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useReducedMotion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { MotionBox } from '../../../components/motion/MotionComponents';
import MoreIcon from '../../../assets/icons/more-horizontal-icon.svg';

import CustomFieldLabel from '../../../components/customfield-label/CustomFieldLabel';
import NoItemsFound from '../../../components/no-items-found/NoItemsFound';
import { useAskPatBeta } from '../../../context';
import { usePagination } from '../../../hooks/usePagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { getProviderAbsences } from '../../../services/absences/providerAbsenceService';
import { getPracticeClosures } from '../../../services/clousures/practiceClosureService';
import { getMarketDetails } from '../../../services/markets/getMarketDetails';
import { updateMarket } from '../../../services/markets/updateMarket';
import { getPracticesList } from '../../../services/practices/getPractices';
import { analytics } from '../../../utils/analytics';
import { Market } from '../../../types/markets/types';
import {
  Practice,
  PracticesResponse,
} from '../../../types/practices/types';
import { applyExternalFilterForNonAdmin } from '../../../utils/externalEntity';

import AskPatChat from '../../../components/ask-pat/AskPatChat';
import MarketResourceLinks from './MarketResourceLinks';
import MarketResourcesSection from './MarketResourcesSection';

interface MarketSnapshot {
  market: Market | undefined;
  clinicClosuresCount: number;
  providerAbsencesCount: number;
}

const MARKET_SNAPSHOT_CACHE_TTL_MS = 5000;
const marketSnapshotCache = new Map<
  string,
  { timestamp: number; data: MarketSnapshot }
>();
const marketSnapshotInFlight = new Map<
  string,
  Promise<MarketSnapshot>
>();

const fetchMarketSnapshot = async (
  marketId: string,
  forceRefresh = false,
): Promise<MarketSnapshot> => {
  const now = Date.now();
  const cached = marketSnapshotCache.get(marketId);

  if (
    !forceRefresh &&
    cached &&
    now - cached.timestamp < MARKET_SNAPSHOT_CACHE_TTL_MS
  ) {
    return cached.data;
  }

  if (!forceRefresh) {
    const inFlight = marketSnapshotInFlight.get(marketId);
    if (inFlight) {
      return inFlight;
    }
  }

  const request = (async () => {
    const { data } = await getMarketDetails(marketId);
    const market = data?.market;

    let clinicClosuresCount = 0;
    let providerAbsencesCount = 0;

    if (market?.id) {
      try {
        const closuresData = await getPracticeClosures({
          marketId: market.id,
        });
        clinicClosuresCount = closuresData.length;
      } catch {
        clinicClosuresCount = 0;
      }

      try {
        const absencesData = await getProviderAbsences({
          marketId: market.id,
        });
        providerAbsencesCount = absencesData.absences.length;
      } catch {
        providerAbsencesCount = 0;
      }
    }

    const snapshot: MarketSnapshot = {
      market,
      clinicClosuresCount,
      providerAbsencesCount,
    };

    marketSnapshotCache.set(marketId, {
      timestamp: Date.now(),
      data: snapshot,
    });

    return snapshot;
  })();

  marketSnapshotInFlight.set(marketId, request);
  try {
    return await request;
  } finally {
    const current = marketSnapshotInFlight.get(marketId);
    if (current === request) {
      marketSnapshotInFlight.delete(marketId);
    }
  }
};

const MarketDetails = () => {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>('');
  const [market, setMarket] = useState<Market | undefined>(undefined);
  const [isMarketLoading, setIsMarketLoading] = useState(true);
  const [clinicClosuresCount, setClinicClosuresCount] = useState(0);
  const [providerAbsencesCount, setProviderAbsencesCount] =
    useState(0);
  const toast = useToast();
  const { isAdmin, canEditInsurance, permissions, isReady } =
    usePermissions();
  const { isAskPatEnabled } = useAskPatBeta();
  const terminateModal = useDisclosure();
  const [isTerminating, setIsTerminating] = useState(false);

  // Track fetch state to prevent duplicate calls
  const lastFetchTimeRef = React.useRef<number>(0);
  const hasFetchedMarketRef = React.useRef<boolean>(false);
  const MIN_REFRESH_INTERVAL = 2000; // Minimum 2 seconds between refreshes

  // Reset fetch tracking when market id changes.
  useEffect(() => {
    hasFetchedMarketRef.current = false;
    lastFetchTimeRef.current = 0;
  }, [id]);

  // Memoize filters to prevent unnecessary re-renders when id changes
  const filters = useMemo<Record<string, string | undefined>>(
    () => ({
      marketIds: id,
    }),
    [id],
  );

  const {
    items: practices,
    loading: isPracticesLoading,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,
  } = usePagination<Practice, PracticesResponse>(
    useCallback(
      (startToken) =>
        getPracticesList(
          startToken,
          applyExternalFilterForNonAdmin(
            filters,
            permissions.isAdmin,
          ),
          10,
        ),
      [filters, permissions.isAdmin],
    ),
    10,
    {},
    filters,
    isReady,
  );

  const fetchMarket = useCallback(
    async (forceRefresh = false) => {
      if (!id) return;

      // Prevent duplicate calls within minimum interval
      const now = Date.now();
      if (
        !forceRefresh &&
        hasFetchedMarketRef.current &&
        now - lastFetchTimeRef.current < MIN_REFRESH_INTERVAL
      ) {
        return;
      }

      lastFetchTimeRef.current = now;
      hasFetchedMarketRef.current = true;

      setIsMarketLoading(true);
      setError(null); // Clear error before refetching
      try {
        const snapshot = await fetchMarketSnapshot(id, forceRefresh);
        setMarket(snapshot.market);
        setClinicClosuresCount(snapshot.clinicClosuresCount);
        setProviderAbsencesCount(snapshot.providerAbsencesCount);
        analytics.trackEvent('view', 'market');
      } catch (error) {
        setError(error as string);
      } finally {
        setIsMarketLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    if (!id) return;
    if (!isReady) return;

    if (searchParams.get('refresh')) return;

    // Only fetch if we haven't fetched yet for this id
    if (hasFetchedMarketRef.current) return;

    fetchMarket();
  }, [id, fetchMarket, searchParams, isReady]);

  // Scroll to top when component mounts or route changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [id]);

  // Force refresh when refresh parameter is present
  useEffect(() => {
    if (searchParams.get('refresh')) {
      fetchMarket(true);
      // Clean up the URL by removing the refresh parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('refresh');
      const newUrl = newSearchParams.toString()
        ? `${window.location.pathname}?${newSearchParams.toString()}`
        : window.location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [searchParams, fetchMarket, navigate]);

  const handleTerminateMarket = useCallback(async () => {
    if (!market || !id) return;

    setIsTerminating(true);
    try {
      await updateMarket({
        id: market.id,
        name: market.name,
        location: market.location || market.state || '',
        nickname: market.nickname || market.name,
        tablespace: market.tablespace || '',
        taxid: market.taxid || '',
        pronunciation: market.pronunciation || market.name,
        pacPhone: (market as unknown as Record<string, unknown>)
          .pacPhone as string | null | undefined,
        resource: (market.resource as Record<string, string>) || {},
        active: false,
        external: market.external ?? false,
      });

      toast({
        title: 'Market terminated successfully',
        description: `${market.name} has been terminated. All linked practices are now inactive.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Navigate away from the terminated market since it becomes invisible
      navigate('/markets');
      terminateModal.onClose();
    } catch (error) {
      // Check if it's the specific "Failed to retrieve markets from DB" error
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      const isRetrievalError = errorMessage.includes(
        'Failed to retrieve the markets from DB',
      );

      if (isRetrievalError) {
        toast({
          title: 'Market terminated successfully',
          description: `${market.name} has been terminated. All linked practices are now inactive. You will be redirected to the markets list.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Navigate away since the market becomes invisible after termination
        navigate('/markets');
        terminateModal.onClose();
      } else {
        toast({
          title: 'Failed to terminate market',
          description: 'Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsTerminating(false);
    }
  }, [market, id, toast, navigate, terminateModal]);

  // Memoize locations to prevent unnecessary re-creation
  const locations = useMemo(() => {
    if (!practices || practices.length === 0) return [];

    return practices.map((practice: Practice) => ({
      name: practice.name,
      id: practice.id,
      address: `${practice.address1}, ${practice.city}, ${practice.state} ${practice.zip}`,
      active: practice.active,
      practiceOnPac: practice.practiceOnPac,
      practiceHospitalFollowupPac: practice.practiceHospitalFollowupPac,
      practiceSickCrossCoveragePac: practice.practiceSickCrossCoveragePac,
    }));
  }, [practices]);

  // Unified loading state: show loader until both are done
  const isFullyLoading = isMarketLoading || isPracticesLoading;

  const formatTaxPacDisplay = (value: string | null | undefined) => {
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ''
    ) {
      return 'N/A';
    }
    return String(value).trim();
  };

  if (error) {
    return (
      <NoItemsFound
        mainMessage="Failed to load Market details"
        detailMessage={
          'An error occurred while fetching the market data. Please try again.'
        }
        refetch={fetchMarket}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {market?.name
            ? `${market.name} | Market | Resource Matrix — Denim Health`
            : 'Market | Resource Matrix — Denim Health'}
        </title>
      </Helmet>
      <MotionBox
        mt="2rem"
        ml="2rem"
        mr="2rem"
        mb="1.62rem"
        initial={
          prefersReducedMotion
            ? { opacity: 1, y: 0 }
            : { opacity: 0, y: 4 }
        }
        animate={{ opacity: 1, y: 0 }}
      >
        <Breadcrumb
          separator={<ChevronRightIcon color="gray.500" />}
        >
          <BreadcrumbItem>
            <BreadcrumbLink
              onClick={() => {
                navigate('/');
              }}
            >
              <Text
                fontSize="0.875rem"
                fontWeight="400"
                lineHeight="1.25rem"
                color="gray.700"
              >
                Home
              </Text>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <Skeleton isLoaded={!isFullyLoading}>
            <BreadcrumbItem>
              <Flex alignItems="center">
                <BreadcrumbLink
                  onClick={() => {
                    navigate(`/states/${market?.state}`);
                  }}
                >
                  <Text
                    fontSize="0.875rem"
                    fontWeight="400"
                    lineHeight="1.25rem"
                    color="gray.700"
                  >
                    {market?.state}
                  </Text>
                </BreadcrumbLink>
                <ChevronRightIcon color="gray.500" ml="0.5rem" />
              </Flex>
            </BreadcrumbItem>
          </Skeleton>

          <Skeleton isLoaded={!isFullyLoading}>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink href="#">
                <Text
                  fontSize="0.875rem"
                  fontWeight="400"
                  lineHeight="1.25rem"
                  color="gray.700"
                >
                  {market?.name}
                </Text>
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Skeleton>
        </Breadcrumb>
      </MotionBox>

      {isFullyLoading ? (
        <>
          {/* Main Content Skeleton */}
          <Flex
            m="2.31rem 2rem 3.5rem 2rem"
            gap="1.5rem"
            justifyContent={'center'}
            alignItems={'flex-start'}
          >
            {/* Left Panel Skeleton */}
            <Flex
              p="1.5rem"
              flexDirection={'column'}
              justifyContent={'center'}
              gap="1.5rem"
              borderRadius={'0.5rem'}
              width="55%"
              background="background.surfaceTeritiary"
            >
              {/* Market Name and Menu Skeleton */}
              <HStack justify="space-between" align="flex-start">
                <Skeleton height="2.5rem" width="20rem" />
                <SkeletonCircle size="2.5rem" />
              </HStack>

              {/* Resources Title Skeleton */}
              <Skeleton height="2rem" width="8rem" />

              {/* Resource Links Skeleton */}
              <VStack spacing="0.75rem" align="stretch">
                <Skeleton height="1.5rem" width="100%" />
                <Skeleton height="1.5rem" width="85%" />
                <Skeleton height="1.5rem" width="90%" />
              </VStack>
            </Flex>

            {/* Map Skeleton */}
            <Flex flex="1" height="100%" alignItems="center">
              <Box width="100%" height="400px">
                <Skeleton
                  height="400px"
                  width="100%"
                  borderRadius="0.5rem"
                />
              </Box>
            </Flex>
          </Flex>

          {/* Market Resources Section Skeleton */}
          <Box m="2rem">
            <VStack spacing="1.5rem" align="stretch">
              {/* Section Title */}
              <Skeleton height="2rem" width="15rem" />

              {/* Resource Cards Grid */}
              <Flex gap="1rem" wrap="wrap">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Box
                    key={i}
                    p="1.5rem"
                    bg="white"
                    borderRadius="0.75rem"
                    border="1px solid"
                    borderColor="gray.200"
                    minW="200px"
                    flex="1"
                  >
                    <VStack spacing="1rem" align="stretch">
                      <Skeleton height="1.5rem" width="80%" />
                      <Skeleton height="1rem" width="60%" />
                      <Skeleton height="2rem" width="100%" />
                    </VStack>
                  </Box>
                ))}
              </Flex>
            </VStack>
          </Box>

          {/* Content Section Skeleton */}
          <Box m="2rem">
            <VStack spacing="1.5rem" align="stretch">
              <Skeleton height="2rem" width="12rem" />
              <Skeleton
                height="300px"
                width="100%"
                borderRadius="0.5rem"
              />
            </VStack>
          </Box>
        </>
      ) : (
        <>
          <MotionBox
            display="flex"
            m="2.31rem 2rem 3.5rem 2rem"
            gap="1.5rem"
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
            initial={
              prefersReducedMotion
                ? { opacity: 1, y: 0 }
                : { opacity: 0, y: 8 }
            }
            animate={{ opacity: 1, y: 0 }}
          >
            <Flex
              p="1.5rem"
              flexDirection={'column'}
              alignItems={'flex-start'}
              gap="1.5rem"
              flex="0 0 65%"
              background="background.surfaceTeritiary"
              width={'100%'}
              borderRadius="0.5rem"
            >
              <HStack justify="space-between" align="flex-start">
                <VStack align="start" spacing="0.5rem">
                  <Text
                    color="text.primary"
                    fontSize="1.875rem"
                    fontWeight={700}
                    lineHeight={'133%'}
                  >
                    {market?.name}
                  </Text>
                  <HStack spacing="0.5rem">
                    <Badge
                      colorScheme={
                        market?.active === false ? 'red' : 'green'
                      }
                      variant="solid"
                      fontSize="0.75rem"
                      px="0.75rem"
                      py="0.25rem"
                      borderRadius="0.375rem"
                    >
                      {market?.active === false
                        ? 'Terminated'
                        : 'Active'}
                    </Badge>
                    {isAdmin() && market?.external && (
                      <Badge
                        variant="solid"
                        fontSize="0.75rem"
                        px="0.75rem"
                        py="0.25rem"
                        borderRadius="0.375rem"
                        bg="orange.100"
                        color="orange.800"
                        borderWidth="1px"
                        borderColor="orange.300"
                      >
                        External
                      </Badge>
                    )}
                  </HStack>
                </VStack>
                <HStack spacing={2}>
                  {(isAdmin() || canEditInsurance()) && (
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<img src={MoreIcon} />}
                        size="sm"
                        aria-label="Options"
                        borderRadius="0.375rem"
                        background={'background.secondary'}
                        width="2.5rem"
                        height="2.5rem"
                        cursor={'pointer'}
                        border="none"
                        transition="all 0.2s ease-in-out"
                      />
                      <MenuList>
                        {(isAdmin() || canEditInsurance()) && (
                          <MenuItem
                            onClick={() =>
                              navigate(`/markets/edit/${id}`)
                            }
                          >
                            Edit Market details
                          </MenuItem>
                        )}
                        {(isAdmin() || canEditInsurance()) && (
                          <MenuItem
                            onClick={() =>
                              navigate(`/markets/${id}/practices`)
                            }
                          >
                            Manage Practices
                          </MenuItem>
                        )}
                        {isAdmin() && market?.active !== false && (
                          <MenuItem
                            onClick={terminateModal.onOpen}
                            color="red.500"
                            _hover={{ bg: 'red.50' }}
                          >
                            Terminate Market
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  )}
                </HStack>
              </HStack>
              <HStack
                spacing={6}
                flexWrap="wrap"
                alignItems="flex-start"
              >
                <CustomFieldLabel
                  customField="Tax ID"
                  fieldValue={formatTaxPacDisplay(market?.taxid)}
                  enableTruncate={false}
                />
                <CustomFieldLabel
                  customField="PAC Phone"
                  fieldValue={formatTaxPacDisplay(market?.pacPhone)}
                  enableTruncate={false}
                />
              </HStack>

              <Text
                color="text.secondary"
                fontSize="1.5rem"
                fontWeight={700}
                lineHeight={'133%'}
              >
                Resources
              </Text>
              {market?.resource && (
                <MarketResourceLinks resources={market?.resource} />
              )}
            </Flex>
            <Flex flex="1" flexDirection="column" gap="1rem">
              {/* Ask PAT Chat Section - Always visible with fixed height */}
              {isAskPatEnabled && id && (
                <Box
                  height="400px"
                  width="100%"
                  maxWidth="100%"
                  overflow="hidden"
                  flexShrink={0}
                >
                  <AskPatChat
                    variant="embedded"
                    docType="market"
                    docId={id}
                    docName={market?.name}
                  />
                </Box>
              )}
            </Flex>
          </MotionBox>
          {id && market && (
            <MotionBox
              initial={
                prefersReducedMotion
                  ? { opacity: 1, y: 0 }
                  : { opacity: 0, y: 8 }
              }
              animate={{ opacity: 1, y: 0 }}
            >
              <MarketResourcesSection
                marketId={id}
                marketName={market.name}
                practices={practices}
                practicesLoading={isPracticesLoading}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPracticesPageChange={handlePageChange}
                practiceCount={market?.practiceCount || 0}
                providerCount={market?.providerCount || 0}
                plansCount={market?.plansCount || 0}
                clinicClosuresCount={clinicClosuresCount}
                providerAbsencesCount={providerAbsencesCount}
                locations={locations}
              />
            </MotionBox>
          )}
        </>
      )}

      {/* Terminate Market Confirmation Modal */}
      <Modal
        isOpen={terminateModal.isOpen}
        onClose={terminateModal.onClose}
        size="md"
      >
        <ModalOverlay
          bg="blackAlpha.600"
          backdropFilter="blur(4px)"
        />
        <ModalContent borderRadius="lg" boxShadow="xl">
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="1rem"
            fontSize="1.125rem"
            fontWeight="600"
            color="red.600"
          >
            Terminate Market
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="1.5rem">
            <VStack spacing="1.5rem" align="stretch">
              <Box
                p="1rem"
                bg="red.50"
                borderRadius="md"
                border="1px solid"
                borderColor="red.200"
              >
                <Text
                  fontSize="0.875rem"
                  color="red.700"
                  fontWeight="500"
                >
                  ⚠️ You are about to terminate "{market?.name}".
                </Text>
                <Text
                  fontSize="0.75rem"
                  color="red.600"
                  fontWeight="600"
                  mt="0.5rem"
                >
                  WARNING: This will make ALL practices in this market
                  inactive and hide the market from view!
                </Text>
              </Box>

              <Text fontSize="0.875rem" color="gray.600">
                This action will:
              </Text>

              <VStack align="start" spacing="0.5rem" pl="1rem">
                <Text
                  fontSize="0.875rem"
                  color="red.600"
                  fontWeight="600"
                >
                  • Make ALL linked practices inactive
                </Text>
                <Text
                  fontSize="0.875rem"
                  color="red.600"
                  fontWeight="600"
                >
                  • Hide this market from all market lists and views
                </Text>
                <Text fontSize="0.875rem" color="gray.600">
                  • Prevent new practices from being added to this
                  market
                </Text>
              </VStack>

              <Text
                fontSize="0.75rem"
                color="red.600"
                fontWeight="500"
              >
                Once terminated, this market cannot be restored. Are
                you sure you want to continue?
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            pt="1rem"
            gap="0.75rem"
          >
            <Button
              variant="ghost"
              onClick={terminateModal.onClose}
              _hover={{ bg: 'gray.100' }}
              transition="all 0.2s ease-in-out"
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleTerminateMarket}
              isLoading={isTerminating}
              loadingText="Terminating..."
              _hover={{ transform: 'translateY(-1px)' }}
              transition="all 0.2s ease-in-out"
            >
              {isTerminating ? 'Terminating...' : 'Terminate Market'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MarketDetails;
